import { useSignal } from "@preact/signals";
import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";
import type { Priority, ReactionSummary } from "../../types";
import {
  IcoArrowDown,
  IcoArrowUp,
  IcoCheckSquare,
  IcoRefresh,
  IcoSquare,
  IcoTrash,
} from "../icons";
import { CommentButton, CommentThread } from "./comment-thread";
import { ReactionBar } from "./reaction-bar";

// ============================================================================
// Types
// ============================================================================

type PriorityItemProps = {
  priority: Priority;
  isFromPrevious?: boolean;
  onToggleComplete?: (id: string, completed: boolean) => void;
  onUpdateTitle?: (id: string, title: string) => void;
  onDelete?: (id: string) => void;
  onCarryForward?: (id: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  readOnly?: boolean;
  /** Workspace ID for comments/reactions (optional - if not provided, features are hidden) */
  workspaceId?: string;
  /** Current user's member ID for comments (optional) */
  currentMemberId?: string;
  /** Initial comment count (optional) */
  commentCount?: number;
  /** Initial reactions (optional) */
  reactions?: ReactionSummary[];
  /** Whether this priority was carried forward to the next period (for manager view) */
  wasCarriedForward?: boolean;
  /** Auto-expand comment thread and scroll to this item (for notification links) */
  autoExpandComments?: boolean;
  /** Disable reacting and commenting (separate from readOnly — e.g. mimic mode). Viewers of others' priorities can still interact. */
  interactionsReadOnly?: boolean;
};

type PrioritySectionProps = {
  title: string;
  description: string;
  children: ComponentChildren;
  /** "previous" = muted retrospective style, "current" = brand-accented forward style */
  variant?: "previous" | "current";
};

type PriorityListProps = {
  priorities: Priority[];
  isFromPrevious?: boolean;
  onToggleComplete?: (id: string, completed: boolean) => void;
  onUpdateTitle?: (id: string, title: string) => void;
  onDelete?: (id: string) => void;
  onReorder?: (orderedIds: string[]) => void;
  onCarryForward?: (id: string) => void;
  readOnly?: boolean;
  /** Workspace ID for comments/reactions (optional - if not provided, features are hidden) */
  workspaceId?: string;
  /** Current user's member ID for comments (optional) */
  currentMemberId?: string;
  /** Comment counts by priority ID (optional) */
  commentCounts?: Record<string, number>;
  /** Reactions by priority ID (optional) */
  reactionsByPriority?: Record<string, ReactionSummary[]>;
  /** Set of priority IDs that were carried to next period (for manager view of previous period) */
  carriedIds?: Set<string>;
  /** Custom empty state message (optional) */
  emptyMessage?: string;
  /** Priority ID to auto-expand comments for (from notification links) */
  highlightPriorityId?: string;
  /** Disable reacting and commenting (separate from readOnly — e.g. mimic mode). */
  interactionsReadOnly?: boolean;
};

type PriorityInputProps = {
  onAdd: (title: string) => void;
  disabled?: boolean;
  /** Called when input text changes (so parent can track unsaved text) */
  onTextChange?: (text: string) => void;
};

// ============================================================================
// PrioritySection - Container with header
// ============================================================================

export function PrioritySection({
  title,
  description,
  children,
  variant,
}: PrioritySectionProps) {
  const isPrevious = variant === "previous";
  return (
    <div
      class={`rounded-2xl p-6 ${
        isPrevious
          ? "bg-surface-page/60 border border-dashed border-border-muted"
          : "bg-gradient-to-br from-[var(--brand-50)]/60 to-surface shadow-sm ring-1 ring-[var(--brand-200)]/30"
      }`}
    >
      <h3
        class={`text-sm font-semibold mb-1 ${isPrevious ? "text-text-muted" : "text-text"}`}
      >
        {title}
      </h3>
      <p class="text-sm text-text-muted mb-4">{description}</p>
      <div class="flex flex-col gap-2">{children}</div>
    </div>
  );
}

// ============================================================================
// PriorityItem - Single priority row
// ============================================================================

export function PriorityItem({
  priority,
  isFromPrevious = false,
  onToggleComplete,
  onUpdateTitle,
  onDelete,
  onCarryForward,
  onMoveUp,
  onMoveDown,
  readOnly = false,
  workspaceId,
  currentMemberId,
  commentCount: initialCommentCount = 0,
  reactions,
  wasCarriedForward = false,
  autoExpandComments = false,
  interactionsReadOnly = false,
}: PriorityItemProps) {
  const isEditing = useSignal(false);
  const editValue = useSignal(priority.title);
  const inputRef = useRef<HTMLInputElement>(null);
  const showComments = useSignal(autoExpandComments);
  const localCommentCount = useSignal(initialCommentCount);
  const itemRef = useRef<HTMLDivElement>(null);

  // Sync with prop when it changes (e.g., from batch fetch)
  useEffect(() => {
    localCommentCount.value = initialCommentCount;
  }, [initialCommentCount]);

  // Auto-expand and scroll when arriving from notification link
  useEffect(() => {
    if (autoExpandComments) {
      showComments.value = true;
      setTimeout(() => {
        itemRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);
    }
  }, [autoExpandComments]);

  const isCompleted = priority.completedAt !== null;
  const isCarried = priority.carriedFromId !== null;
  const hasCommentsFeature = workspaceId && currentMemberId;

  const handleStartEdit = () => {
    if (readOnly) return;
    editValue.value = priority.title;
    isEditing.value = true;
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSaveEdit = () => {
    const newTitle = editValue.value.trim();
    if (newTitle && newTitle !== priority.title) {
      onUpdateTitle?.(priority.id, newTitle);
    }
    isEditing.value = false;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      isEditing.value = false;
    }
  };

  return (
    <div ref={itemRef}>
      <div
        class={`flex items-center gap-3 px-3 py-2 rounded-lg bg-surface-muted/50 hover:bg-surface-muted transition-colors ${
          isCompleted ? "opacity-60" : ""
        }`}
      >
        {/* Checkbox */}
        {readOnly ? (
          <span class="flex-shrink-0">
            {isCompleted ? (
              <IcoCheckSquare class="size-5 text-green-600/60 dark:text-green-400/60" />
            ) : (
              <IcoSquare class="size-5 text-text-subtle" />
            )}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onToggleComplete?.(priority.id, !isCompleted)}
            class="flex-shrink-0 cursor-pointer"
          >
            {isCompleted ? (
              <IcoCheckSquare class="size-5 text-green-600 dark:text-green-400" />
            ) : (
              <IcoSquare class="size-5 text-text-muted hover:text-text" />
            )}
          </button>
        )}

        {/* Title */}
        {isEditing.value ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue.value}
            onInput={(e) => {
              editValue.value = e.currentTarget.value;
            }}
            onBlur={handleSaveEdit}
            onKeyDown={handleKeyDown}
            maxLength={500}
            class={`flex-1 bg-transparent border-none outline-none text-text ${
              isCompleted ? "line-through" : ""
            }`}
          />
        ) : (
          <button
            type="button"
            onClick={handleStartEdit}
            disabled={readOnly}
            class={`flex-1 text-left text-text ${isCompleted ? "line-through" : ""} ${
              readOnly
                ? "cursor-default"
                : "cursor-text hover:bg-surface-muted rounded px-1 -mx-1"
            }`}
          >
            {priority.title}
          </button>
        )}

        {/* Carried indicator with lineage info */}
        {isCarried && (
          <span
            class={`flex items-center gap-1 ${
              priority.carryCount && priority.carryCount > 1
                ? "text-amber-600 dark:text-amber-400"
                : "text-text-muted"
            }`}
            title={
              priority.carryCount && priority.carryCount > 1
                ? `Carried ${priority.carryCount - 1}× since ${priority.originDate ? new Date(priority.originDate).toLocaleDateString() : "unknown"}`
                : "Carried forward from previous period"
            }
          >
            <IcoRefresh class="size-4" />
            {priority.carryCount && priority.carryCount > 1 && (
              <span class="text-xs font-medium">
                {priority.carryCount - 1}×
              </span>
            )}
          </span>
        )}

        {/* Delete button */}
        {!readOnly && !isFromPrevious && (
          <button
            type="button"
            onClick={() => onDelete?.(priority.id)}
            class="text-text-muted/40 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            <IcoTrash class="size-4" />
          </button>
        )}

        {/* Reorder arrows */}
        {(onMoveUp || onMoveDown) && (
          <div class="flex items-center">
            {onMoveUp && (
              <button
                type="button"
                onClick={onMoveUp}
                class="p-0.5 rounded text-text-muted/40 hover:text-text-muted transition-colors"
              >
                <IcoArrowUp class="size-3.5" />
              </button>
            )}
            {onMoveDown && (
              <button
                type="button"
                onClick={onMoveDown}
                class="p-0.5 rounded text-text-muted/40 hover:text-text-muted transition-colors"
              >
                <IcoArrowDown class="size-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Comment button - shown after other actions */}
        {hasCommentsFeature && (
          <CommentButton
            count={localCommentCount.value}
            isOpen={showComments.value}
            onClick={() => {
              showComments.value = !showComments.value;
            }}
          />
        )}
      </div>

      {/* Carry forward / copy to this week button for previous period items */}
      {isFromPrevious && onCarryForward && (
        <div class="ml-12 mt-1.5">
          {wasCarriedForward ? (
            <span class="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <IcoRefresh class="size-3" />
              {isCompleted ? "Copied to this week" : "Already carried"}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onCarryForward(priority.id)}
              class="text-xs text-text-muted hover:text-[var(--brand-600)] dark:hover:text-[var(--brand-400)] flex items-center gap-1 transition-colors"
            >
              <IcoRefresh class="size-3" />
              {isCompleted ? "Copy to this week" : "Carry forward"}
            </button>
          )}
        </div>
      )}

      {/* "Carried to this week" annotation for manager view (read-only) */}
      {wasCarriedForward && isFromPrevious && !onCarryForward && (
        <div class="ml-12 mt-1 text-xs text-text-muted flex items-center gap-1">
          <span class="text-text-subtle">→</span>
          <span>Carried to this week</span>
        </div>
      )}

      {/* Reactions row */}
      {hasCommentsFeature && (
        <div class="mt-2 ml-12">
          <ReactionBar
            workspaceId={workspaceId!}
            targetType="priority"
            targetId={priority.id}
            initialReactions={reactions}
            readOnly={interactionsReadOnly}
            compact
          />
        </div>
      )}

      {/* Comment thread (expandable) */}
      {hasCommentsFeature && showComments.value && (
        <div class="mt-3 ml-12 p-4 rounded-lg bg-surface-muted border border-border">
          <CommentThread
            workspaceId={workspaceId!}
            targetType="priority"
            targetId={priority.id}
            currentMemberId={currentMemberId!}
            readOnly={interactionsReadOnly}
            onCommentCountChange={(count) => {
              localCommentCount.value = count;
            }}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PriorityList - List of priorities (draggable)
// ============================================================================

export function PriorityList({
  priorities,
  isFromPrevious = false,
  onToggleComplete,
  onUpdateTitle,
  onDelete,
  onReorder,
  onCarryForward,
  readOnly = false,
  workspaceId,
  currentMemberId,
  commentCounts,
  reactionsByPriority,
  carriedIds,
  emptyMessage = "No priorities yet",
  highlightPriorityId,
  interactionsReadOnly = false,
}: PriorityListProps) {
  const handleMove = (index: number, direction: -1 | 1) => {
    if (!onReorder) return;
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= priorities.length) return;
    const ids = priorities.map((p) => p.id);
    const [removed] = ids.splice(index, 1);
    if (removed) {
      ids.splice(newIndex, 0, removed);
      onReorder(ids);
    }
  };

  if (priorities.length === 0) {
    return <p class="text-sm text-text-muted italic py-2">{emptyMessage}</p>;
  }

  return (
    <div class="flex flex-col gap-2">
      {priorities.map((priority, i) => (
        <div key={priority.id}>
          <PriorityItem
            priority={priority}
            isFromPrevious={isFromPrevious}
            onToggleComplete={onToggleComplete}
            onUpdateTitle={onUpdateTitle}
            onDelete={onDelete}
            onCarryForward={onCarryForward}
            onMoveUp={
              !readOnly && onReorder && i > 0
                ? () => handleMove(i, -1)
                : undefined
            }
            onMoveDown={
              !readOnly && onReorder && i < priorities.length - 1
                ? () => handleMove(i, 1)
                : undefined
            }
            readOnly={readOnly}
            workspaceId={workspaceId}
            currentMemberId={currentMemberId}
            commentCount={commentCounts?.[priority.id] ?? 0}
            reactions={reactionsByPriority?.[priority.id]}
            wasCarriedForward={carriedIds?.has(priority.id)}
            autoExpandComments={priority.id === highlightPriorityId}
            interactionsReadOnly={interactionsReadOnly}
          />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// PriorityInput - Add new priority
// ============================================================================

export function PriorityInput({
  onAdd,
  disabled = false,
  onTextChange,
}: PriorityInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = () => {
    const input = inputRef.current;
    if (!input) return;

    const title = input.value.trim();
    if (!title) return;

    onAdd(title);
    input.value = "";
    onTextChange?.("");
    input.focus();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      class="flex items-center gap-2"
    >
      <input
        ref={inputRef}
        type="text"
        name="priority"
        placeholder="Enter a priority..."
        disabled={disabled}
        maxLength={500}
        onKeyDown={handleKeyDown}
        onInput={(e) => onTextChange?.(e.currentTarget.value)}
        class="flex-1 px-3 py-2 rounded-lg border border-border bg-surface text-text placeholder:text-text-muted focus:outline-none focus:border-[var(--brand-500)] focus:ring-1 focus:ring-[var(--brand-500)] transition-colors"
      />
      <button
        type="submit"
        disabled={disabled}
        class="px-4 py-2 rounded-lg text-sm font-medium bg-surface-raised border border-border text-text-secondary hover:bg-surface-alt hover:text-text hover:border-text-muted focus:outline-none focus:ring-2 focus:ring-[var(--brand-500)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Add
      </button>
    </form>
  );
}
